import { apiSlice as api } from "./rtk-api/apiSlice"

const injectedRtkApi = api.injectEndpoints({
  endpoints: (builder) => ({
    completeOrganizationSetup: builder.mutation<CreateCompanyResponseType, { name: string; countryCode: string }>({
      query: (data) => ({ url: "/v1/company/setup", method: "POST", data }),
    }),
    createCompany: builder.mutation<CreateCompanyResponseType, CreateCompanyRequest>({
      query: (data) => ({
        url: "/v1/company",
        method: "POST",
        data,
      }),
      invalidatesTags: ["Companies"],
    }),
    updateCompany: builder.mutation<UpdateCompanyResponseType, { id: number } & UpdateCompanyRequest>({
      query: ({ id, ...data }) => ({
        url: `/v1/company/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["Companies"],
    }),
    deleteCompany: builder.mutation<DeleteCompanyResponseType, number>({
      query: (id) => ({
        url: `/v1/company/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Companies"],
    }),
    updateParentCompany: builder.mutation<UpdateCompanyResponseType, UpdateParentCompanyRequest>({
      query: (data) => ({
        url: `/v1/company/parent`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["GetParentCompany", "SetupJourney"],
    }),
    getParentCompanyAndTenant: builder.query<GetParentCompanyAndTenantResponseType, void>({
      query: () => ({
        url: `/v1/company/parent`,
        method: "GET",
      }),
      providesTags: ["GetParentCompany"],
    }),
    getCompany: builder.query<GetCompanyResponseType, number>({
      query: (id) => ({
        url: `/v1/company/${id}`,
        method: "GET",
      }),
      providesTags: ["Companies"],
    }),
    getParentCompanyCompletion: builder.query<GetCompanyPercentageResponseType, void>({
      query: () => ({
        url: `/v1/company/parent/completion-percentage`,
        method: "GET",
      }),
      providesTags: ["GetParentCompany"],
    }),
    getCompanies: builder.query<GetCompaniesResponseType, void>({
      query: () => ({
        url: `/v1/company`,
        method: "GET",
      }),
      providesTags: ["Companies"],
    }),
    getCompanyCompletion: builder.query<GetCompletionPercentageResponseType, void>({
      query: () => ({
        url: "/v1/company/completion-percentage",
        method: "GET",
      }),
      providesTags: ["Companies"],
    }),
  }),
})

export const {
  useCreateCompanyMutation,
  useCompleteOrganizationSetupMutation,
  useUpdateParentCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetCompanyQuery,
  useGetParentCompanyAndTenantQuery,
  useGetCompaniesQuery,
  useGetParentCompanyCompletionQuery,
  useGetCompanyCompletionQuery,
} = injectedRtkApi

export default injectedRtkApi

// Interfaces for Requests and Responses
export interface CreateCompanyRequest {
  name: string
  registrationNumber: string
  website?: string
  gicsCode?: string | null
  isCustomGicsCode?: boolean
  addressLine?: string
  postalCode?: string
  city?: string
  countryCode: string
  legalForm?: string
  currencyCode?: string
  ownershipType?: string
  equityPercentage?: number
  operationalControl?: boolean
  financialControl?: boolean
  financialControlPercentage?: number | null
  operationalControlPercentage?: number | null
  type?: string
}

export interface CreateCompanyResponseType {
  success: boolean
  error?: string
}

export interface UpdateParentCompanyRequest {
  registrationNumber?: string
  name?: string
  website?: string
  gicsCode?: string
  isCustomGicsCode?: boolean
  countryOfRegistrationCode?: string
  legalForm?: string
  corporateGroupId?: number
  currencyCode?: string
  yearOfFoundation?: number
  logoUrl?: string
  csrdReportingMarketListed?: boolean
  csrdReportingBalance?: boolean
  csrdReportingTurnover?: boolean
  csrdReportingEmployees?: boolean
  headquarteredOutsideEU?: boolean
  reportingEUTurnover?: boolean
  reportingNetTurnover?: boolean
  reportingEUSubsidiary?: boolean
  hasBaseYear?: boolean
  baseYear?: number | null
  baseYearEmissions?: number | null
  baseYearFile?: string | null
  isPartOfCorporateGroup?: boolean
  hasSubsidiary?: boolean
  hasIncorporatedJointVenture?: boolean
  hasAffiliated?: boolean
  hasNonIncorporatedJointVenture?: boolean
  isFranchisor?: boolean
  isMultinational?: boolean
  inMultipleJurisdictions?: boolean
}

export interface UpdateCompanyRequest {
  name?: string
  registrationNumber?: string
  website?: string
  gicsCode?: string
  isCustomGicsCode?: boolean
  countryOfRegistrationCode?: string
  legalForm?: string
  corporateGroupId?: number
  currencyCode?: string
  yearOfFoundation?: number
  ownershipType?: string
  equityPercentage?: number
  operationalControl?: boolean
  financialControl?: boolean
  financialControlPercentage?: number | null
  operationalControlPercentage?: number | null
}

export interface UpdateCompanyResponseType {
  success: boolean
  error?: string
}

export interface DeleteCompanyResponseType {
  success: boolean
  error?: string
}

export interface GetCompletionPercentageResponseType {
  success: boolean
  error: string | null
  percentage: number
  filledFields: number
  totalFields: number
  entityCount: number
  perEntity: { id: number | string; percentage: number }[]
}

export interface GetCompanyPercentageResponseType {
  success: boolean
  error?: string
  total: number
  company: number
  location: number
  tenant: number
}

export interface GetCompanyResponseType {
  success: boolean
  error?: string
  company: Company
}

export interface GetCompaniesResponseType {
  success: boolean
  error?: string
  companies: Company[]
}

export interface GetParentCompanyAndTenantResponseType {
  success: boolean
  error?: string
  company: ParentCompany
}

export interface ParentCompany {
  id: number
  name: string
  registrationNumber: string
  logoUrl?: string | null
  countryOfRegistration?: { code: string; name: string } | null
  countryOfRegistrationCode: string
  legalForm: string
  website: string | null
  gicsCode: string
  isCustomGicsCode: boolean
  corporateGroupId: number | null
  currency?: { code: string; name: string } | null
  currencyCode: string
  yearOfFoundation: number
  isParentCompany: boolean
  hasBaseYear?: boolean
  baseYear?: number | null
  csrdReportingCompany?: boolean
  csrdReportingBalance?: boolean
  csrdReportingTurnover?: boolean
  csrdReportingEmployees?: boolean
  csrdReportingMarketListed?: boolean
  headquarteredOutsideEU?: boolean
  reportingEUTurnover?: boolean
  reportingNetTurnover?: boolean
  reportingEUSubsidiary?: boolean
  baseYearEmissions?: number | null
  baseYearFile: string | null
  isPartOfCorporateGroup?: boolean
  hasSubsidiary?: boolean
  hasIncorporatedJointVenture?: boolean
  hasAffiliated?: boolean
  hasNonIncorporatedJointVenture?: boolean
  isFranchisor?: boolean
  isMultinational?: boolean
  inMultipleJurisdictions?: boolean
  type: string
  locationsCount?: number
}

export interface Company {
  id: number
  name: string
  registrationNumber: string
  countryOfRegistration?: { code: string; name: string } | null
  countryOfRegistrationCode: string
  legalForm: string
  legalFormLabel: string
  website: string | null
  gicsCode: string
  isCustomGicsCode: boolean
  corporateGroupId: number | null
  currency?: { code: string; name: string } | null
  currencyCode: string
  yearOfFoundation: number
  type: string
  isParentCompany: boolean
  ownershipType: string | null
  equityPercentage: number | null
  operationalControl: boolean | null
  financialControl: boolean | null
  financialControlPercentage: number | null
  operationalControlPercentage: number | null
  locationsCount?: number
}
